import React, { createContext, useReducer, useEffect, useMemo, useCallback, useContext } from 'react';
import settingsReducer, { initialSettings } from './settingsReducer';

export const SettingsContext = createContext();

export const SettingsProvider = ({ children, appKey = 'genericAppSettings' }) => {
  const [settings, dispatch] = useReducer(settingsReducer, initialSettings, (defaultInitialSettings) => {
    try {
      const localData = localStorage.getItem(appKey);
      return localData ? JSON.parse(localData) : defaultInitialSettings;
    } catch (error) {
      console.warn(`Error reading settings from localStorage for ${appKey}:`, error);
      return defaultInitialSettings;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(appKey, JSON.stringify(settings));
    } catch (error) {
      console.warn(`Error saving settings to localStorage for ${appKey}:`, error);
    }
  }, [settings, appKey]);

  // --- Helper functions ---
  const updateSetting = useCallback((keyOrUpdates, value) => {
    // If keyOrUpdates is an object, it's a batch update
    if (typeof keyOrUpdates === 'object' && keyOrUpdates !== null && value === undefined) {
      dispatch({ type: 'UPDATE_MULTIPLE_SETTINGS', payload: keyOrUpdates });
    } else if (typeof keyOrUpdates === 'string') { // Else, it's a single key-value update
      dispatch({ type: 'UPDATE_SETTING', payload: { key: keyOrUpdates, value } });
    } else {
      console.warn('Invalid arguments for updateSetting. Provide a key/value pair or an object of settings.');
    }
  }, [dispatch]);

  const resetAllSettings = useCallback(() => {
    dispatch({ type: 'RESET_SETTINGS' });
  }, [dispatch]);

  // Renamed for clarity: replaces all current settings with the provided object
  const replaceAllSettings = useCallback((newSettings) => {
    dispatch({ type: 'REPLACE_SETTINGS', payload: newSettings });
  }, [dispatch]);

  const logCurrentSettings = useCallback(() => {
    console.log('Current Application Settings:', settings);
  }, [settings]);
  // --- End Helper functions ---

  const contextValue = useMemo(() => ({
    settings,
    updateSetting,
    resetAllSettings,
    replaceAllSettings, // Updated name
    logCurrentSettings,
  }), [settings, updateSetting, resetAllSettings, replaceAllSettings, logCurrentSettings]);

  return (
    <SettingsContext.Provider value={contextValue}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};